import { Duration } from "luxon";
import { LIGHTNING_LIST_PATH } from "./MeteomaticsApiConst";
import { ColorMap, CoordinateSystem, FormatMimeType, IsoLinesRequest_toGridRequest, LightningListRequest_toGridRequest, Positions, selectExclusiveIsoBandAndLineParams, } from "./models";
import { concatParameterUnits } from "./utility";
export const USE_DECLUTTERED_TRUE = "use_decluttered=true";
/**
 * Models urls in the meteomatics API format. This API is special as it allows REST GET requests to be executed
 * through POST requests. This is desirable if the URL is constructed programmatically since browsers have pretty low limits for the URL length.
 * In this case the parameters are partitioned as follows:
 *
 * ```
 * URL: https://api.meteomatics.com/<validdatetime>/
 * Post-Body: <parameters>/<location>/<format>?<optionals> in plain text
 * ```
 */
export class MeteomaticsApiUrl {
    constructor(pathname, body) {
        this.pathname = pathname;
        this.body = body;
    }
    /**
     * Build a URL for the Meteomatics WMS server
     *
     * @param request specification of the grid including location, resolution, time.
     * @returns Meteomatics api url transformable to a string using `toString`.
     */
    static forWms(request) {
        const datetime = request.datetime.toUTC().toISO();
        const parameter = concatParameterUnits(request.parameters);
        const model = request.model;
        const formatMimeType = FormatMimeType.WEBP;
        // only on webp
        const initDate = request.initDate ? `&init_date=${request.initDate}` : "";
        const qualityFactor = formatMimeType === FormatMimeType.WEBP ? "&webp_quality_factor=80" : "";
        const colorMapParameter = request.colorMap != null ? `&STYLES=${ColorMap[request.colorMap]}` : "";
        const ensSelect = request.ensSelect ? `&ens_select=${request.ensSelect}` : "";
        const calibrated = `&calibrated=${!!request.calibrated}`;
        const verticalInterpolation = MeteomaticsApiUrl.formatVerticalInterpolationForQuery(request.vertical_interpolation);
        const { crsName, boundingBox } = MeteomaticsApiUrl.formatAreaForWms(request.area);
        const pathname = MeteomaticsApiUrl.appendRawQuerySuffix(`wms?VERSION=1.3.0&Time=${datetime}&REQUEST=GetMap&FORMAT=${formatMimeType}${qualityFactor}&LAYERS=${model}:${parameter}&MODEL=${model}&bbox=${boundingBox}${colorMapParameter}${ensSelect}&crs=${crsName}&width=${request.width}&height=${request.height}${calibrated}&${USE_DECLUTTERED_TRUE}${verticalInterpolation}${initDate}`, request.apiUrlSuffix);
        const body = "";
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forWindVector(gridFormat, request) {
        //TODO: set all props in url
        const datetime = request.datetime.toUTC().toISO();
        const parameters = concatParameterUnits(request.parameters);
        // const location = MeteomaticsApiUrl.formatAreaWithSamplingStrategyForApi({ ...request });
        const location = ""; // MeteomaticsApiUrl.formatAreaWithSamplingStrategyForApi({ ...request });
        const resolution = `${request.width}x${request.height}`;
        const model = request.model;
        const bbox = MeteomaticsApiUrl.formatAreaWithSamplingStrategyForApi(request); // no width and height
        const options = [`source=${model}`];
        options.push(`bbox=${bbox}`);
        if (request.ensSelect) {
            options.push(`ens_select=${request.ensSelect}`);
        }
        options.push(`calibrated=${!!request.calibrated}`);
        options.push(USE_DECLUTTERED_TRUE);
        MeteomaticsApiUrl.appendVerticalInterpolationOption(options, request.vertical_interpolation);
        const pathname = `${datetime}`;
        const body = `${request.parameters.join(",")}/${bbox}:${resolution}/json`;
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forVectorLayerStyle(request) {
        const { parameter, vectorTileData } = request;
        const pathname = `mvt/${vectorTileData}/${parameter}/style.json`;
        const body = "";
        return new MeteomaticsApiUrl(pathname, body);
    }
    /**
     * Build URL for the Meteomatics Vector Tile server
     *
     * @param request specification of the tile
     * @return Meteomatics api url transformable to a string using `toString`.
     */
    // TODO Vector tiles typize
    static forVectorTile(request) {
        const { datetime, geometry, model, vectorTileData, ensSelect, apiUrlSuffix, ...rest } = request;
        const optionalParameters = Object.keys(rest)
            .filter((parameter) => rest[parameter] !== undefined &&
            !!rest[parameter]?.toString().length)
            .map((parameter) => `${parameter}=${rest[parameter]}`);
        if (request.ensSelect) {
            optionalParameters.push(`ens_select=${request.ensSelect}`);
        }
        optionalParameters.push(USE_DECLUTTERED_TRUE);
        const modelParam = model ? `&model=${model}` : "";
        const pathname = `mvt/${vectorTileData}/${request.parameter}/tile/${geometry.z}/${geometry.x}/${geometry.y}.pbf?datetime=${datetime.toUTC().toISO()}${modelParam}&${optionalParameters.join("&")}`;
        const body = "";
        return new MeteomaticsApiUrl(MeteomaticsApiUrl.appendRawQuerySuffix(pathname, apiUrlSuffix), body);
    }
    /**
     * Build a URL for WFS GetFeature endpoints. Always this endpoint always returns XML format.
     * @param request
     */
    static forWfs(request) {
        const parameters = request.parameters ? concatParameterUnits(request.parameters) : null;
        const datetime = request.datetime?.toUTC().toISO();
        const model = request.model;
        const area = MeteomaticsApiUrl.formatAreaForWfs(request.area);
        const requestType = request.requestType;
        const pathname = `wfs?SERVICE=WFS&VERSION=1.0.0&REQUEST=${requestType}&TYPENAME=${model}${parameters ? `&PARAMETERS=${parameters}` : ""}&BBOX=${area}${datetime ? `&TIME=${datetime}` : ""}`;
        return new MeteomaticsApiUrl(pathname);
    }
    /**
     * Build a URL for WFS GetCapabilities. Always this endpoint always returns XML format.
     * @param request
     */
    static forWfsCapabilities(request) {
        const { requestType } = request;
        const body = `wfs?SERVICE=WFS&VERSION=1.0.0&REQUEST=${requestType}`;
        return new MeteomaticsApiUrl("", body);
    }
    static forAvailableTimeRange(model, parameterList) {
        const parameters = concatParameterUnits(parameterList);
        const body = `get_time_range?model=${model}&parameters=${parameters}`;
        return new MeteomaticsApiUrl(body);
    }
    static forInitialTime(model, parameterList, validDatetime) {
        const parameters = concatParameterUnits(parameterList);
        const dateIso = validDatetime.toISOString();
        const body = `get_init_date?model=${model}&valid_date=${dateIso}&parameters=${parameters}`;
        return new MeteomaticsApiUrl(body);
    }
    static forPoint(singlePointFormat, request) {
        let datetime = request.startDatetime.toUTC().toISO();
        if (request.duration && request.temporalResolution) {
            datetime = `${datetime + request.duration.toISO()}:${request.temporalResolution.toISO()}`;
        }
        else {
            // default 7 Days and 1h resolution;
            datetime = `${datetime + Duration.fromISO("P7D").toISO()}:${Duration.fromISO("PT1H").toISO()}`;
        }
        const parameters = concatParameterUnits(request.parameters);
        const model = request.model;
        const location = MeteomaticsApiUrl.formatCoordinatesForApi(request);
        const format = singlePointFormat.format;
        const options = [`source=${model}`];
        options.push(`calibrated=${!!request.requestOptions?.calibrated}`);
        if (request.requestOptions?.on_invalid) {
            options.push(`on_invalid=${request.requestOptions.on_invalid}`);
        }
        if (request.ensSelect) {
            options.push(`ens_select=${request.ensSelect}`);
        }
        if (request.initDate) {
            options.push(`init_date=${request.initDate}`);
        }
        options.push(USE_DECLUTTERED_TRUE);
        options.push("on_invalid=fill_with_invalid");
        const pathname = `${datetime}`;
        const body = MeteomaticsApiUrl.appendRawQuerySuffix(`${parameters}/${location}/${format}?${options.join("&")}`, request.apiUrlSuffix);
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forPolygon(singlePointFormat, request) {
        let datetime = request.startDatetime.toUTC().toISO();
        if (request.duration && request.temporalResolution) {
            datetime = `${datetime + request.duration.toISO()}:${request.temporalResolution.toISO()}`;
        }
        else {
            // default 7 Days and 1h resolution;
            datetime = `${datetime + Duration.fromISO("P7D").toISO()}:${Duration.fromISO("PT1H").toISO()}`;
        }
        const parameters = concatParameterUnits(request.parameters);
        const model = request.model;
        const polygonString = MeteomaticsApiUrl.formatPolygonForApi(request);
        const format = singlePointFormat.format;
        const options = [`source=${model}`];
        options.push(`calibrated=${!!request.requestOptions?.calibrated}`);
        options.push(USE_DECLUTTERED_TRUE);
        if (request.ensSelect) {
            options.push(`ens_select=${request.ensSelect}`);
        }
        const pathname = `${datetime}`;
        const body = MeteomaticsApiUrl.appendRawQuerySuffix(`${parameters}/${polygonString}/${format}?${options.join("&")}`, request.apiUrlSuffix);
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forPointAndTime(singlePointFormat, request) {
        // TODO: datetime range
        const datetime = request.startDatetime.toUTC().toISO();
        const parameters = concatParameterUnits(request.parameters);
        const model = request.model;
        const location = MeteomaticsApiUrl.formatCoordinatesForApi(request);
        const format = singlePointFormat.format;
        const options = [`source=${model}`];
        options.push(`calibrated=${!!request.requestOptions?.calibrated}`);
        if (request.ensSelect) {
            options.push(`ens_select=${request.ensSelect}`);
        }
        if (request.initDate) {
            options.push(`init_date=${request.initDate}`);
        }
        options.push(USE_DECLUTTERED_TRUE);
        const pathname = `${datetime}`;
        const body = MeteomaticsApiUrl.appendRawQuerySuffix(`${parameters}/${location}/${format}?${options.join("&")}`, request.apiUrlSuffix);
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forGrid(gridFormat, request) {
        const datetime = request.datetime.toUTC().toISO();
        const parameters = concatParameterUnits(request.parameters);
        const location = MeteomaticsApiUrl.formatAreaWithSamplingStrategyForApi({ ...request });
        const resolution = `${request.width}x${request.height}`;
        const model = request.model;
        const bbox = MeteomaticsApiUrl.formatAreaForGrid(request); // no width and height
        const format = MeteomaticsApiUrl.formatGridFormat(gridFormat);
        const options = [`source=${model}`];
        options.push(`bbox=${bbox}`);
        if (request.ensSelect) {
            options.push(`ens_select=${request.ensSelect}`);
        }
        if (request.initDate) {
            options.push(`init_date=${request.initDate}`);
        }
        if (format === "bin") {
            options.push("on_invalid=fill_with_invalid");
        }
        options.push(`calibrated=${!!request.calibrated}`);
        MeteomaticsApiUrl.appendVerticalInterpolationOption(options, request.vertical_interpolation);
        options.push(USE_DECLUTTERED_TRUE);
        const pathname = `${datetime}`;
        const body = MeteomaticsApiUrl.appendRawQuerySuffix(`${parameters}/${location}${resolution && `:${resolution}`}/${format}?${options.join("&")}`, request.apiUrlSuffix);
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forColorMap(request) {
        const parameter = concatParameterUnits([request.parameter]);
        const colorMapParameter = request.style != null ? `&style=${ColorMap[request.style]}` : "";
        const ensSelect = request.ensSelect !== "" ? `&ens_select=${request.ensSelect}` : "";
        const verticalInterpolation = MeteomaticsApiUrl.formatVerticalInterpolationForQuery(request.vertical_interpolation);
        const pathname = `get_colormap?parameter=${parameter}${colorMapParameter}${ensSelect}&format=csv&calibrated=${!!request.calibrated}&${USE_DECLUTTERED_TRUE}${verticalInterpolation}`;
        return new MeteomaticsApiUrl(pathname);
    }
    static forIsoLines(request) {
        const datetime = request.datetime.toUTC().toISO();
        const parameter = request.parameter;
        const model = request.model;
        const bbox = MeteomaticsApiUrl.formatAreaForGrid(IsoLinesRequest_toGridRequest(request));
        const pathname = request.path;
        const isoParams = selectExclusiveIsoBandAndLineParams(request);
        let body = `?datetime=${datetime}&parameter=${parameter}&model=${model}&bbox=${bbox}`;
        body += `&height=${request.height}&width=${request.width}`;
        if (isoParams.isobandIntervals) {
            body += `&isoband_intervals=${encodeURIComponent(isoParams.isobandIntervals)}`;
        }
        else if (isoParams.isolineValues) {
            body += `&isoline_values=${encodeURIComponent(isoParams.isolineValues)}`;
        }
        else if (isoParams.isolineRange) {
            body += `&isoline_range=${encodeURIComponent(isoParams.isolineRange)}`;
        }
        if (request.radiusMedianFilter != null) {
            body += `&radius_median_filter=${request.radiusMedianFilter}`;
        }
        if (request.radiusGaussianFilter != null) {
            body += `&radius_gaussian_filter=${request.radiusGaussianFilter}`;
        }
        if (request.resolution) {
            body += `&resolution=${encodeURIComponent(request.resolution)}`;
        }
        body += `&calibrated=${!!request.calibrated}`;
        if (request.ensSelect) {
            body += `&ens_select=${request.ensSelect}`;
        }
        body += `&${USE_DECLUTTERED_TRUE}`;
        return new MeteomaticsApiUrl(pathname, MeteomaticsApiUrl.appendRawQuerySuffix(body, request.apiUrlSuffix));
    }
    static forWeatherFronts(request) {
        const datetime = request.datetime.toUTC().toISO();
        const model = request.model;
        const bbox = MeteomaticsApiUrl.formatAreaForGrid(IsoLinesRequest_toGridRequest(request));
        const pathname = request.path;
        let body = `?datetime=${datetime}&model=${model}&bbox=${bbox}`;
        body += `&calibrated=${!!request.calibrated}`;
        if (request.ensSelect) {
            body += `&ens_select=${request.ensSelect}`;
        }
        body += `&${USE_DECLUTTERED_TRUE}`;
        return new MeteomaticsApiUrl(pathname, body);
    }
    /**
     * Build a URL for any GeoJSON-style endpoint driven by a `path` on the request.
     * Example: req.path = "get_weatherfronts" | "get_earthquakes"
     */
    static forGeoJsonEndpoint(req) {
        const pathname = req.path;
        // Decide which standard params to include for this endpoint
        const policy = MeteomaticsApiUrl.geoJsonParamPolicy(pathname);
        // Standard parameters according to policy
        const params = MeteomaticsApiUrl.buildCommonGeoJsonParams(req, policy);
        // Layer-specific / ad-hoc parameters (e.g., time_range for earthquakes)
        if (req.query) {
            for (const [k, v] of Object.entries(req.query)) {
                if (v !== undefined && v !== null) {
                    params.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
                }
            }
        }
        const body = params.length ? `?${params.join("&")}` : "";
        return new MeteomaticsApiUrl(pathname, body);
    }
    static appendVerticalInterpolationOption(options, verticalInterpolation) {
        const opt = MeteomaticsApiUrl.formatVerticalInterpolationOption(verticalInterpolation);
        if (opt) {
            options.push(opt);
        }
    }
    static formatVerticalInterpolationOption(verticalInterpolation) {
        if (verticalInterpolation === "none") {
            return `vertical_interpolation=${verticalInterpolation}`;
        }
        return "";
    }
    static formatVerticalInterpolationForQuery(verticalInterpolation) {
        const opt = MeteomaticsApiUrl.formatVerticalInterpolationOption(verticalInterpolation);
        return opt ? `&${opt}` : "";
    }
    static appendRawQuerySuffix(base, rawSuffix) {
        const trimmedSuffix = rawSuffix?.trim();
        if (!trimmedSuffix) {
            return base;
        }
        const normalizedSuffix = trimmedSuffix.replace(/^[?&]+/, "");
        if (!normalizedSuffix) {
            return base;
        }
        const separator = base.includes("?") ? "&" : "?";
        return `${base}${separator}${normalizedSuffix}`;
    }
    /** Decide which standard params to include for a given GeoJSON endpoint. */
    static geoJsonParamPolicy(pathname) {
        switch (pathname) {
            // Earthquakes only needs bbox (time_range comes via query)
            case "get_earthquakes":
                return { datetime: false, model: false, ensSelect: false, calibrated: false, size: false, bbox: true };
            default:
                return { datetime: true, model: true, ensSelect: true, calibrated: true, size: true, bbox: true };
        }
    }
    /**
     * Build standard query parameters that most GeoJSON endpoints share, with a policy mask.
     */
    static buildCommonGeoJsonParams(req, policy) {
        const params = [];
        // datetime (if requested by policy)
        if (policy.datetime && req.datetime) {
            // guard against undefined toUTC/toISO on non-DateTime inputs
            const iso = req.datetime?.toUTC?.().toISO?.();
            if (iso) {
                params.push(`datetime=${encodeURIComponent(iso)}`);
            }
        }
        // model options
        if (policy.model && req.model) {
            params.push(`model=${encodeURIComponent(req.model)}`);
        }
        if (policy.ensSelect && req.ensSelect) {
            params.push(`ens_select=${encodeURIComponent(req.ensSelect)}`);
        }
        if (policy.calibrated && typeof req.calibrated === "boolean") {
            params.push(`calibrated=${req.calibrated ? "true" : "false"}`);
        }
        // viewport hints some endpoints use
        if (policy.size && req.width && req.height) {
            params.push(`size=${req.width}x${req.height}`);
        }
        // bbox is required for endpoints where policy.bbox = true
        if (policy.bbox) {
            if (!req.area) {
                throw new Error("GeoJSON endpoint requires 'area' (bbox) but it was not provided.");
            }
            const wgs84Area = req.area.project(CoordinateSystem.WGS84);
            const bbox = MeteomaticsApiUrl.formatAreaForWfs(wgs84Area);
            params.push(`bbox=${bbox}`);
        }
        return params;
    }
    static forLightningList(request) {
        const datetime = request.datetime.toUTC().toISO();
        const duration = request.timeDuration.toISO();
        const format = request.format ?? "json";
        const bbox = MeteomaticsApiUrl.formatAreaWithSamplingStrategyForApi(LightningListRequest_toGridRequest(request));
        const pathname = LIGHTNING_LIST_PATH;
        let body = `?time_range=${datetime}${duration}&bounding_box=${bbox}&format=${format}`;
        if (request.enable_feature) {
            body += "&enable_feature=mm_lightning_v2";
        }
        return new MeteomaticsApiUrl(pathname, body);
    }
    static forMetars({ icao, startDateTime, endDateTime }) {
        const pathname = `get_metars?time_range=${startDateTime.toISO()}--${endDateTime.toISO()}&icao_code=${icao}`;
        return new MeteomaticsApiUrl(pathname);
    }
    static forLegend(request) {
        let pathname = `wms?VERSION=1.3.0&REQUEST=GetLegendGraphic&FORMAT=image/svg+xml&LAYER=${request.parameter}&TRANSPARENT=${request.transparent}`;
        if (request.style) {
            pathname += `&STYLE=${request.style}`;
        }
        return new MeteomaticsApiUrl(pathname);
    }
    static formatAreaForWfs(area) {
        const latMax = MeteomaticsApiUrl.formatLat(area.latMax);
        const latMin = MeteomaticsApiUrl.formatLat(area.latMin);
        const lngMax = MeteomaticsApiUrl.formatLng(area.lngMax);
        const lngMin = MeteomaticsApiUrl.formatLng(area.lngMin);
        return `${lngMin},${latMin},${lngMax},${latMax}`;
    }
    static formatGridFormat(format) {
        if ("colorMap" in format && format.colorMap != null) {
            return `${format.format}_${ColorMap[format.colorMap]}`;
        }
        return format.format;
    }
    /**
     * Format lat for the API.
     * Clamp values between [-90 and 90]
     *
     * @param lat
     * @private
     */
    static formatLat(lat) {
        if (lat < -90) {
            return -90;
        }
        if (lat > 90) {
            return 90;
        }
        return lat.toFixed(8);
    }
    /**
     * Format lng value for the API.
     *
     * Clamp values between [-180 and 180]
     *
     * @param lng
     * @private
     */
    static formatLng(lng) {
        if (lng < -180) {
            return -180;
        }
        if (lng > 180) {
            return 180;
        }
        return lng.toFixed(8);
    }
    static formatCoordinatesForApi(request) {
        const coordinates = request.coordinates.map((coords) => coords.project(CoordinateSystem.WGS84));
        return coordinates
            .map((coord) => {
            return `${coord.lat},${coord.lng}`;
        })
            .join("+");
    }
    static formatPolygonForApi(request) {
        const positionsList = request.feature.geometry.coordinates.map((coordinates) => {
            const positions = new Positions(coordinates, CoordinateSystem.WGS84);
            return positions.project(CoordinateSystem.WGS84);
        });
        return positionsList
            .map((positions) => {
            const coords = positions.positions
                .map(([lng, lat]) => {
                return `${lat},${lng}`;
            })
                .join("_");
            return `${coords}:${request.aggregate}`;
        })
            .join("+");
    }
    static formatAreaWithSamplingStrategyForApi(request) {
        const area = request.area.project(CoordinateSystem.WGS84).applySamplingStrategy(request.sampling, request);
        const latMax = MeteomaticsApiUrl.formatLat(area.latMax);
        const latMin = MeteomaticsApiUrl.formatLat(area.latMin);
        const lngMax = MeteomaticsApiUrl.formatLng(area.lngMax);
        const lngMin = MeteomaticsApiUrl.formatLng(area.lngMin);
        return `${latMax},${lngMin}_${latMin},${lngMax}`;
    }
    static formatAreaForGrid(request) {
        const area = request.area.project(CoordinateSystem.WGS84).applySamplingStrategy(request.sampling, request);
        const latMax = MeteomaticsApiUrl.formatLat(area.latMax);
        const latMin = MeteomaticsApiUrl.formatLat(area.latMin);
        const lngMax = MeteomaticsApiUrl.formatLng(area.lngMax);
        const lngMin = MeteomaticsApiUrl.formatLng(area.lngMin);
        return `${lngMin},${latMin},${lngMax},${latMax}`;
    }
    static formatAreaForWms(area) {
        const latMax = area.latMax; //this.formatLat(area.latMax);
        const latMin = area.latMin; //this.formatLat(area.latMin);
        const lngMax = area.lngMax; //this.formatLng(area.lngMax);
        const lngMin = area.lngMin; //this.formatLng(area.lngMin);
        switch (area.crs) {
            case CoordinateSystem.WGS84:
                return {
                    crsName: "EPSG:4326",
                    boundingBox: [latMin, lngMin, latMax, lngMax].join(","),
                };
            case CoordinateSystem.EPSG3857:
                return {
                    crsName: "EPSG:3857",
                    boundingBox: [lngMin, latMin, lngMax, latMax].join(","),
                };
            case CoordinateSystem.EPSG4326:
                return {
                    crsName: "EPSG:4326",
                    boundingBox: [latMin, lngMin, latMax, lngMax].join(","),
                };
            default:
                // TODO: `never` does not work here because of the generic
                throw Error("entered unreachable code region");
        }
    }
    /**
     * Format the URL as a string. This URL can be used to make a GET request to the API.
     */
    toString() {
        return this.body ? `${this.pathname}/${this.body}` : this.pathname;
    }
}
//# sourceMappingURL=MeteomaticsApiUrl.js.map